import React, { useState, useMemo } from "react";
import clsx from "clsx";
import { Grid, Paper, Button, TextField } from "@material-ui/core";
import TermsAndConditions from "../../components/Modals/TermsAndConditions";
import LicensePlateDetails from "../../components/LicensePlateDetail/LicensePlateDetails";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import CallCenterService from "../../services/CallCenterService";
import TicketService from "../../services/TicketService";
import apiClient from "../../auth/apiClient";
import { useHistory } from "react-router-dom";
import { useStyles } from "../../pages/LicensePlateSearchPage/LicensePlateSearchPage.styles";

const LicensePlateSearchManagement = ({entityID}) => {
    const history = useHistory();
    const classes = useStyles();
    const enqueueSnackbar = useEnqueueSnackbar();

    const callCenterService = useMemo(() => new CallCenterService(apiClient), []);
    const ticketService = useMemo(() => new TicketService(apiClient), []);

    const [searchTerm, setSearchTerm] = useState("");
    const [ticketId, setTicketId] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [imageData, setImageData] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [isImageLoading, setIsImageLoading] = useState(false);

    const handleTicketClick = (ticketId) => {
        history.push(`/mobilepay/${ticketId}`);
    };

    const fetchLPRImage = async (lprID, forCaptureImage) => {
        try {
            setIsImageLoading(true);
            const response = forCaptureImage
                ? await callCenterService.getLicensePlateCaptureImage(lprID)
                : await callCenterService.getLicensePlateContextImage(lprID);

            const r = new FileReader();
            r.readAsDataURL(response.data);
            r.onload = () => {
                setImageData(r.result);
            };
        } catch (err) {
            setImageData(null);
            enqueueSnackbar("Failed to load image", {
                variant: "error",
                tag: "FailedToLoadLPRImage",
            });
        }finally {
            setIsImageLoading(false);
        }
    };

    const handleSearchClick = async () => {
        if (!searchTerm.trim()) { return; }

        try {
            setIsLoading(true);
            setHasSearched(true);

            const response = await ticketService.getTicketByLicensePlate(
                entityID,
                searchTerm
            );

            if (response.status === 200 && response.data) {
                setSearchResults(response.data);
                setTicketId(response.data.ticketID);

                let forCaptureImage = false;
                if (!response.data.contextImage && response.data.captureImage) {
                    forCaptureImage = true;
                }
                fetchLPRImage(response.data.lprId, forCaptureImage);
            } else {
                setSearchResults(null);
            }
        } catch (error) {
            enqueueSnackbar("Error getting license plate details", {
                variant: "error",
                tag: "FailedToGetLicensePlateDetails",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchChange = (value) => {
        setHasSearched(false);
        setSearchTerm(value);
        setSearchResults(null);
        setImageData(null);
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper
                    elevation={4}
                    className={clsx(classes.paperContainerLargeWithText, "license-plate-instructions")}
                >
                    Enter your license plate to search and pay for your parking.
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <div className={clsx(classes.searchContainer, "license-plate-search-container")}>
                    <TextField
                        className={clsx([classes.searchTerm, "search-term"])}
                        id="search-term"
                        placeholder="Enter License Plate"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                    />
                </div>
            </Grid>

            <Grid item xs={12}>
                <Button
                    variant="contained"
                    color="primary"
                    className={clsx(classes.payNowButton, "search-license-button")}
                    onClick={handleSearchClick}
                    disabled={isLoading}
                >
                    <div>Search</div>
                </Button>
            </Grid>

            <Grid item xs={12} className={clsx(classes.flexCenter)}>
                <div className={clsx(classes.termsAndConditions, "terms-and-conditions")}>
                    <TermsAndConditions />
                </div>
            </Grid>

            <Grid item xs={12}>
                {!isLoading && hasSearched && !searchResults && (
                    <Paper
                        elevation={4}
                        className={clsx(classes.paperContainerLargeWithText, "license-plate-search-info")}
                    >
                        No Entry Record Found
                    </Paper>
                )}
                {!isLoading && searchResults && (
                    <Paper
                        key={ticketId}
                        elevation={4}
                        className={clsx(classes.paperContainerLarge, "license-plate-search-info")}
                        onClick={() => handleTicketClick(ticketId)}
                        style={{ cursor: "pointer" }}
                    >
                        <LicensePlateDetails
                            licensePlate={searchResults.licensePlate}
                            ticketId={ticketId}
                            startTime={searchResults.entranceTime}
                            imageData={imageData}
                            isImageLoading={isImageLoading}
                        />
                    </Paper>
                )}
            </Grid>
        </Grid>
    );
};

export default LicensePlateSearchManagement;
